import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions, getErrorCode } from '@onefootprint/fetch-dashboard';
import { LoadingSpinner } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { ReactFlowProvider } from '@xyflow/react';
import { parseAsBoolean, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import TenantSwitcher from '@/components/entities/components/details/components/tenant-switcher';
import ErrorComponent from '@/components/error';
import PlaybookBuilder, { useStore } from '@/components/playbook-builder';
import ArtifactsViewer from '@/features/artifacts-viewer';
import { Header } from './components/header';
import NoGraphEmptyState from './components/no-graph-empty-state';

/** `FpErrorCode::OnboardingHasNoGraph` — the onboarding never ran a graph, so no trace can exist. */
const NO_GRAPH_ERROR_CODE = 'T131';
type OnboardingTraceProps = {
  fpId: string;
  onboardingId: string;
  entityKind: 'user' | 'business';
};
const OnboardingTrace = t0 => {
  const $ = _c(39);
  const {
    fpId,
    onboardingId,
    entityKind
  } = t0;
  const store = useStore();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = parseAsBoolean.withDefault(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [isArtifactsViewerOpen, setIsArtifactsViewerOpen] = useQueryState("artifacts", t1);
  let t2;
  if ($[1] !== fpId || $[2] !== onboardingId) {
    t2 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId,
        onboardingId
      }
    });
    $[1] = fpId;
    $[2] = onboardingId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!onboardingId;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    isPending,
    error,
    data: trace
  } = useQuery(t4);
  let t5;
  bb0: {
    if (!trace?.entries) {
      let t6;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[8] !== trace.entries) {
      t6 = trace.entries.flatMap(_temp);
      $[8] = trace.entries;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = t6;
  }
  const allArtifacts = t5;
  let t6;
  if ($[10] !== store || $[11] !== trace) {
    t6 = () => {
      if (!trace) {
        return;
      }
      store.reset({
        trace
      });
    };
    $[10] = store;
    $[11] = trace;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== trace) {
    t7 = [trace];
    $[13] = trace;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  useEffect(t6, t7);
  let t8;
  if ($[15] !== allArtifacts || $[16] !== entityKind || $[17] !== fpId || $[18] !== onboardingId || $[19] !== setIsArtifactsViewerOpen || $[20] !== trace) {
    t8 = trace && <ReactFlowProvider><PlaybookBuilder className="h-full" headerComponent={<Header playbook={trace.playbook} kind={trace.kind} status={trace.status} deactivated={trace.deactivated} artifactsCount={allArtifacts.length} onViewArtifacts={() => setIsArtifactsViewerOpen(true)} fpId={fpId} entityKind={entityKind} onboardingId={onboardingId} />} /></ReactFlowProvider>;
    $[15] = allArtifacts;
    $[16] = entityKind;
    $[17] = fpId;
    $[18] = onboardingId;
    $[19] = setIsArtifactsViewerOpen;
    $[20] = trace;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== isPending) {
    t9 = isPending && <div className="flex h-full items-center justify-center"><LoadingSpinner /></div>;
    $[22] = isPending;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  let t10;
  if ($[24] !== entityKind || $[25] !== error || $[26] !== fpId || $[27] !== onboardingId) {
    t10 = error && (getErrorCode(error) === NO_GRAPH_ERROR_CODE ? <NoGraphEmptyState fpId={fpId} onboardingId={onboardingId} entityKind={entityKind} /> : <TenantSwitcher entityId={fpId} Loading={_temp2}><ErrorComponent error={error} /></TenantSwitcher>);
    $[24] = entityKind;
    $[25] = error;
    $[26] = fpId;
    $[27] = onboardingId;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== allArtifacts || $[30] !== isArtifactsViewerOpen || $[31] !== setIsArtifactsViewerOpen || $[32] !== trace) {
    t11 = trace && <ArtifactsViewer open={isArtifactsViewerOpen} onClose={() => setIsArtifactsViewerOpen(null)} artifacts={allArtifacts} />;
    $[29] = allArtifacts;
    $[30] = isArtifactsViewerOpen;
    $[31] = setIsArtifactsViewerOpen;
    $[32] = trace;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11 || $[36] !== t8 || $[37] !== t9) {
    t12 = <div className="flex flex-col h-full min-h-0 overflow-hidden">{t8}{t9}{t10}{t11}</div>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t8;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
export default OnboardingTrace;
function _temp(entry) {
  return entry.onboardingData ?? [];
}
function _temp2() {
  return <LoadingSpinner />;
}